import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { StandardDrink } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface BaseStandardDrinkWatchProps {
    standardDrink?: StandardDrink;
    standardDrinkId?: number;
}

export function StandardDrinkInput({ disabled = false }: Props) {
    const { standardDrinkId, standardDrink } =
        useFormContext<BaseStandardDrinkWatchProps>().getValues();

    const standardDrinkQueryResult = useQuery({
        queryKey: ['standardDrinks', standardDrinkId],
        queryFn: () =>
            Api.StandardDrinks.StandardDrink.getStandardDrinks({
                page: 0,
                size: 1000,
                sort: 'name,asc',
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: standardDrink ? [standardDrink] : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Standard Drink"
            name="standardDrinkId"
            loading={standardDrinkQueryResult.isLoading}
            multiple={false}
            options={standardDrinkQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: StandardDrink,
                    value?: StandardDrink | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },
                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.name}`;
                },
                getOptionKey(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.id}`;
                },
            }}
        />
    );
}
